import { useMemo } from 'react';
import { BulkAddClientRecord, CSVHeaders, csvHeaders, headerFromProp, propFromHeader } from '../types';
import { validateRecord } from './utils';
import { FlatList, ScrollView } from 'react-native';
import { GridRefs } from '../../../../../v2BaseComponents/RichTable/types';
import { columnWidths } from './constants';

export type TableError = {
    rowIndex: number;
    column: CSVHeaders;
};

export type UseTableErrorsArgs = {
    verticalFlatListRef: FlatList | null;
    horizontalScrollViewRef: ScrollView | null;
    gridInputRefs: GridRefs;
    records: BulkAddClientRecord[];
};

export type UseTableErrorsReturnValue = {
    tableErrorCount: number;
    onFocusNextError: () => void;
    isFocusNextErrorDisabled: boolean;
};

async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const useTableErrors = ({
    records,
    verticalFlatListRef,
    horizontalScrollViewRef,
    gridInputRefs,
}: UseTableErrorsArgs): UseTableErrorsReturnValue => {
    const tableErrors: TableError[] = useMemo(() => {
        const tableErrors: TableError[] = [];
        records.forEach((record, index) => {
            const recordErrors = validateRecord(record);
            Object.keys(recordErrors).forEach((column) => {
                if (recordErrors[column as keyof BulkAddClientRecord]) {
                    tableErrors.push({
                        rowIndex: index,
                        column: headerFromProp(column as keyof BulkAddClientRecord),
                    });
                }
            });
        });
        return tableErrors;
    }, [records]);

    const onFocusNextError = () => {
        async function waitFor(check: () => Boolean, maxTime: number) {
            const delay = 100;
            let tryCount = Math.ceil(maxTime / delay);
            do {
                await sleep(delay);
            } while (!check() && tryCount-- > 0);
        }

        async function progressivelyFocus() {
            if (!verticalFlatListRef || !horizontalScrollViewRef) {
                return;
            }

            const sortedErrors = tableErrors.sort((a, b) =>
                a.column === b.column
                    ? a.rowIndex - b.rowIndex
                    : csvHeaders.indexOf(a.column) - csvHeaders.indexOf(b.column),
            );
            const focusedError = sortedErrors[0];
            if (!focusedError) {
                return;
            }

            const { rowIndex, column } = focusedError;
            verticalFlatListRef.scrollToIndex({ index: rowIndex, animated: true });
            const columnIndex = csvHeaders.indexOf(column);
            const getInputRef = () => gridInputRefs?.[rowIndex]?.[columnIndex];
            await waitFor(() => !!getInputRef(), 3000);
            const inputRef = getInputRef();
            if (!inputRef || !horizontalScrollViewRef) {
                console.error('Could not focus on error');
                return;
            }

            // get column horizontal offset
            const precedingColumns = csvHeaders.slice(0, columnIndex);
            const columnOffsets = precedingColumns.reduce((acc, header) => {
                const columnWidth = columnWidths[propFromHeader(header)];
                return acc + columnWidth; // 2 is the border width
            }, 0);
            horizontalScrollViewRef.scrollTo({ x: columnOffsets, animated: true });
            await sleep(500);
            inputRef.focus();
        }

        progressivelyFocus();
    };

    return {
        tableErrorCount: tableErrors.length,
        isFocusNextErrorDisabled: tableErrors.length === 0 || !verticalFlatListRef || !horizontalScrollViewRef,
        onFocusNextError,
    };
};

export const useHasErrors = (records: BulkAddClientRecord[]): boolean => {
    return records.some((record) => Object.values(validateRecord(record)).some((value) => !!value));
};
